import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import moment from "moment";
import logo from "../../assets/Main_Logo_AI_Colored_Padding.png";

const C = {
  green: "#14833b",
  greenDark: "#2e7d32",
  orange: "#ef6c00",
  gray50: "#fafafa",
  gray100: "#f5f6f7",
  gray200: "#eef0f1",
  gray300: "#e5e7eb",
  text: "#111827",
  sub: "#6b7280",
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 24,
    paddingHorizontal: 24,
    paddingBottom: 72,
    fontFamily: "Helvetica",
    fontSize: 12,
  },

  title: { fontSize: 22, fontWeight: 700, color: C.green, textAlign: "center", marginBottom: 8 },
  targetRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  targetLabel: { fontSize: 12, fontWeight: 700, color: C.text },
  targetChip: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: C.gray300,
    backgroundColor: "#fff",
    paddingVertical: 4,
    paddingHorizontal: 10,
    fontSize: 11,
  },

  card: {
    borderWidth: 1,
    borderColor: C.gray200,
    backgroundColor: C.gray100,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 14,
    marginBottom: 12,
  },
  section: {
    borderWidth: 1,
    borderColor: C.gray200,
    backgroundColor: "#fff",
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 14,
    marginBottom: 12,
  },
  sectionTitle: { fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 8 },

  pill: {
    borderRadius: 999,
    paddingVertical: 3,
    paddingHorizontal: 10,
    fontSize: 11,
    fontWeight: 700,
    alignSelf: "flex-start",
  },
  pillYes: { backgroundColor: C.greenDark, color: "#fff" },
  pillNo: { backgroundColor: C.orange, color: "#fff" },

  gradeLabel: { fontSize: 12, color: C.sub, marginBottom: 2 },
  gradeCircle: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderColor: "#dcedc8",
    color: '#000',
    textAlign: "center",
    lineHeight: 22,
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 6,
  },

  bullet: { fontSize: 12, marginBottom: 6 },

  table: {
    borderWidth: 1,
    borderColor: C.gray300,
    borderRadius: 8,
    overflow: "hidden",
  },
  thead: {
    flexDirection: "row",
    backgroundColor: C.gray100,
    borderBottomWidth: 1,
    borderBottomColor: C.gray300,
  },
  th: { flex: 1, fontSize: 11, fontWeight: 700, paddingVertical: 8, paddingHorizontal: 10 },
  row: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: C.gray300,
  },
  td: { flex: 1, fontSize: 11, paddingVertical: 8, paddingHorizontal: 10, color: C.text },

  kvRow: {
    flexDirection: "row",
    borderTopWidth: 1,
    borderTopColor: C.gray300,
  },
  kvKey: {
    width: "40%",
    fontSize: 11,
    fontWeight: 700,
    paddingVertical: 8,
    paddingHorizontal: 10,
    backgroundColor: C.gray100,
    borderRightWidth: 1,
    borderRightColor: C.gray300,
  },
  kvVal: { flex: 1, fontSize: 11, paddingVertical: 8, paddingHorizontal: 10 },

  footer: {
    position: "absolute",
    bottom: 16,
    left: 24,
    right: 24,
    height: 24,
    borderTopWidth: 1,
    borderTopColor: C.gray300,
    paddingTop: 6,
    fontSize: 9,
    color: C.sub,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  footerLogo: { width: 60 },
});

const Footer = () => {
  const dateText = new Date().toLocaleDateString("en-US");
  return (
    <View style={styles.footer} fixed>
      <Text>{`Generated by Portend AI • Confidential • ${dateText}`}</Text>
      <Image style={styles.footerLogo} src={logo} />
    </View>
  );
};

const DNSReportPDF = ({ data, pageTitle = "DNS Report" }) => {
  const r = data?.result || {};
  const grades = data?.grades || {};
  const hasSRV = (r.srv || []).length > 0;
  const hasCAA = (r.caa || []).length > 0;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>{pageTitle}</Text>

        <View style={styles.targetRow}>
          <Text style={styles.targetLabel}>Target:</Text>
          <Text style={styles.targetChip}>{data?.target || "-"}</Text>
        </View>

        <View style={styles.card}>
          <Text style={styles.sectionTitle}>Grades</Text>
          <Text style={styles.gradeLabel}>DNS Grade</Text>
          <Text>{grades?.dnsGrade || "-"}</Text>
          {(grades?.reason || []).map((reason, i) => (
            <Text key={i} style={styles.bullet}>- {reason}</Text>
          ))}
        </View>

        <View style={[styles.card, { backgroundColor: "#eef5ff" }]}>
          <Text style={styles.sectionTitle}>Scan Results</Text>
          <Text>Latency: {r.latency || "-"}</Text>
          <Text>
            TCP Support:{" "}
            <Text style={[styles.pill, r.tcpSupport ? styles.pillYes : styles.pillNo]}>
              {r.tcpSupport ? "Yes" : "No"}
            </Text>
          </Text>
          <Text>
            Recursive Query Allowed:{" "}
            <Text style={[styles.pill, r.recursiveQueryAllowed ? styles.pillYes : styles.pillNo]}>
              {r.recursiveQueryAllowed ? "Yes" : "No"}
            </Text>
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Name Servers</Text>
          {(r.nameservers || []).map((ns, i) => (
            <Text key={i} style={styles.bullet}>{ns}</Text>
          ))}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Mail Exchange (MX) Records</Text>
          {(r.mx || []).length > 0 ? r.mx.map((mx, i) => (
            <Text key={i} style={styles.bullet}>{mx}</Text>
          )) : <Text>None</Text>}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>PTR Records</Text>
          {(r.ptr || []).length > 0 ? r.ptr.map((ptr, i) => (
            <Text key={i} style={styles.bullet}>{ptr}</Text>
          )) : <Text>None</Text>}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>A Records</Text>
          {(r.aRecords || []).length > 0 ? r.aRecords.map((rec, i) => (
            <Text key={i} style={styles.bullet}>{rec}</Text>
          )) : <Text>None</Text>}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>AAAA Records</Text>
          {(r.aaaaRecords || []).length > 0 ? r.aaaaRecords.map((rec, i) => (
            <Text key={i} style={styles.bullet}>{rec}</Text>
          )) : <Text>None</Text>}
        </View>

        {hasSRV && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>SRV Records</Text>
            <View style={styles.table}>
              <View style={styles.thead}>
                <Text style={styles.th}>Service</Text>
                <Text style={styles.th}>Protocol</Text>
                <Text style={styles.th}>Priority</Text>
                <Text style={styles.th}>Port</Text>
                <Text style={styles.th}>Target</Text>
              </View>
              {r.srv.map((srv, i) => (
                <View key={i} style={styles.row}>
                  <Text style={styles.td}>{srv.service}</Text>
                  <Text style={styles.td}>{srv.protocol}</Text>
                  <Text style={styles.td}>{srv.priority}</Text>
                  <Text style={styles.td}>{srv.port}</Text>
                  <Text style={styles.td}>{srv.target}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {hasCAA && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>CAA Records</Text>
            {r.caa.map((rec, i) => (
              <Text key={i} style={styles.bullet}>{rec.tag}: {rec.value}</Text>
            ))}
          </View>
        )}

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>SOA Record</Text>
          {r.soa ? (
            <View style={{ borderWidth: 1, borderColor: C.gray300, borderRadius: 8, overflow: "hidden" }}>
              <View style={styles.kvRow}><Text style={styles.kvKey}>Primary Nameserver</Text><Text style={styles.kvVal}>{r.soa.primaryNameserver}</Text></View>
              <View style={styles.kvRow}><Text style={styles.kvKey}>Admin Email</Text><Text style={styles.kvVal}>{r.soa.adminEmail}</Text></View>
              <View style={styles.kvRow}><Text style={styles.kvKey}>Serial Number</Text><Text style={styles.kvVal}>{r.soa.serialNumber}</Text></View>
              <View style={styles.kvRow}><Text style={styles.kvKey}>Refresh Interval</Text><Text style={styles.kvVal}>{r.soa.refresh} seconds</Text></View>
              <View style={styles.kvRow}><Text style={styles.kvKey}>Retry Interval</Text><Text style={styles.kvVal}>{r.soa.retry} seconds</Text></View>
              <View style={styles.kvRow}><Text style={styles.kvKey}>Expire Time</Text><Text style={styles.kvVal}>{r.soa.expire} seconds</Text></View>
              <View style={styles.kvRow}><Text style={styles.kvKey}>Minimum TTL</Text><Text style={styles.kvVal}>{r.soa.minimumTTL} seconds</Text></View>
            </View>
          ) : <Text>None</Text>}
        </View>

        <Footer />
      </Page>
    </Document>
  );
};

export default DNSReportPDF;
